import React from 'react'
// libs
import { useSelector, useDispatch } from 'react-redux'
import { change, formValueSelector, isDirty } from 'redux-form'
// hooks
import { useModalData } from 'hooks/index'
// components
import { Modal } from 'components/UI'
// view components
import RecurrenceForm from 'components/Modals/KPIRecurrenceModal/Form'
// constants
import {
  Forms,
  Modals,
  REPEAT_VALUES,
  REPEAT_OPTIONS,
  RECURRENCE_ENDS_VALUES,
  EACH_PERIOD_ENDS_TYPE_OPTIONS
} from 'constants/index'
// utils
import moment from 'moment'
import { trimTz } from 'utils/date'
import { generateKPIRecurrenceRanges } from 'utils/kpi'
import { getOptionByValue, getValueFromOption } from 'utils/form'

const PAPER_PROPS = {
  style: {
    margin: 0,
    overflow: 'auto'
  }
}

const formSelector = formValueSelector(Forms.SCHEDULED_RISK_RECURRENCE_FORM)

const RiskRecurrenceModal: React.FC = () => {
  const dispatch = useDispatch()

  const [isLoading, setLoading] = React.useState(false)

  const {
    hideModal,
    modalData: { isOpen, name }
  } = useModalData({ modalType: Modals.SCHEDULED_RISK_RECURRENCE_MODAL })

  const {
    days,
    recurrence,
    repeatEveryType,
    repeatEveryValue,
    isCurrentFormDirty,
    eachPeriodEndsType,
    firstPeriodStartsAt
  } = useSelector((state) => ({
    days: formSelector(state, `days`),
    recurrence: formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)(state, name),
    repeatEveryType: formSelector(state, `repeatEveryType`) || {},
    repeatEveryValue: formSelector(state, `repeatEveryValue`),
    eachPeriodEndsType: formSelector(state, `eachPeriodEndsType`),
    isCurrentFormDirty: isDirty(Forms.SCHEDULED_RISK_RECURRENCE_FORM)(state),
    firstPeriodStartsAt: formSelector(state, `firstPeriodStartsAt`)
  }))

  const initialValues = React.useMemo(() => {
    if (recurrence && Object.keys(recurrence).length > 1) {
      return {
        ...recurrence,
        repeatEveryType: getOptionByValue(recurrence.repeatEveryType, REPEAT_OPTIONS),
        eachPeriodEndsType: getOptionByValue(recurrence.eachPeriodEndsType, EACH_PERIOD_ENDS_TYPE_OPTIONS)
      }
    }

    return { active: true }
  }, [recurrence])

  const formattedFirstPeriod = firstPeriodStartsAt ? moment(firstPeriodStartsAt).format('L') : null

  // methods
  const handleSubmit = async (data) => {
    try {
      setLoading(true)

      const recurrence = {
        ...data,
        repeatEveryType: getValueFromOption(data.repeatEveryType),
        eachPeriodEndsType: getValueFromOption(data.eachPeriodEndsType),
        recurrenceEndsAt: data.recurrenceEnds === RECURRENCE_ENDS_VALUES.ON ? data.recurrenceEndsAt : null
      }

      dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, name, recurrence))

      hideModal()
    } catch (error) {
      console.log('[ScheduledRiskRecurrenceModal.handleSubmit]: ', error)
    } finally {
      setLoading(false)
    }
  }

  const changeField = (field, value, form) => dispatch(change(form, field, value))

  const recalculateDates = () => {
    if (isCurrentFormDirty) {
      const startPoint = moment(firstPeriodStartsAt)

      if (!firstPeriodStartsAt) {
        changeField(`firstPeriodStartsAt`, trimTz(moment().startOf('d')), Forms.SCHEDULED_RISK_RECURRENCE_FORM)

        return
      }

      let shouldCalculate = false

      if (repeatEveryType.value === REPEAT_VALUES.WEEKLY) {
        shouldCalculate = Array.isArray(days) && !!days.length
      }

      if (repeatEveryType.value === REPEAT_VALUES.MONTHLY) {
        shouldCalculate = !!eachPeriodEndsType
      }

      if (shouldCalculate) {
        const { lastDayOfThePeriod } = generateKPIRecurrenceRanges(
          { repeatEveryValue, repeatEveryType, days, eachPeriodEndsType },
          startPoint
        )

        changeField(`firstPeriodEndsAt`, trimTz(lastDayOfThePeriod.startOf('d')), Forms.SCHEDULED_RISK_RECURRENCE_FORM)
      }
    }
  }

  React.useEffect(recalculateDates, [days, eachPeriodEndsType, formattedFirstPeriod, repeatEveryType, repeatEveryValue])

  return (
    <Modal
      width='xs'
      isOpen={isOpen}
      hideModal={hideModal}
      disableRestoreFocus
      data-testid='recurrence-modal'
      PaperProps={PAPER_PROPS}>
      <RecurrenceForm
        isRisk
        hideModal={hideModal}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        repeatEveryType={repeatEveryType.value}
        form={Forms.SCHEDULED_RISK_RECURRENCE_FORM}
      />
    </Modal>
  )
}

export default RiskRecurrenceModal
